import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useTreatments } from "@/hooks/useTreatments";
import { useBillingPortal } from "@/hooks/useBillingPortal";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PageHeader } from "@/components/PageHeader";
import { CancelSubscriptionModal } from "@/components/CancelSubscriptionModal";

const profileSchema = z.object({
  first_name: z.string().min(1, "Required").max(80),
  last_name:  z.string().min(1, "Required").max(80),
  phone:      z.string().max(30).optional().or(z.literal("").transform(() => undefined)),
});
type ProfileForm = z.infer<typeof profileSchema>;

const emailSchema = z.object({
  email: z.string().email("Enter a valid email"),
});
type EmailForm = z.infer<typeof emailSchema>;

export default function AccountSettings() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);

  if (patientQ.isLoading) return <LoadingScreen />;
  const patient = patientQ.data;
  if (!patient || !session) {
    return (
      <AppLayout>
        <PageHeader title="Account" />
        <Alert tone="error">Something went wrong loading your account.</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader title="Account" subtitle="Manage your sign-in and profile details." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EmailCard currentEmail={session.user.email ?? ""} />
        <ProfileCard patient={patient} />
        <div className="lg:col-span-2">
          <SubscriptionsCard patientId={patient.id} hasStripe={!!patient.stripe_customer_id} />
        </div>
      </div>

      {/* Discreet link to the dedicated Privacy & data page. The full HIPAA
          / CCPA / CPRA rights menu used to render inline here, which made
          the Account page feel like a wall of compliance text every visit.
          Moved to /account/privacy + linked here and in the footer, so it's
          one click away without dominating the page. */}
      <p className="mt-8 text-[13px] text-fh-gray text-center">
        Looking for cookie preferences or to exercise your HIPAA / CCPA
        rights?{" "}
        <Link
          to="/account/privacy"
          className="text-fh-green hover:text-fh-green-dark font-semibold underline"
        >
          Open Privacy &amp; data settings →
        </Link>
      </p>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Subscriptions / cancellation -----------------------------------------------
// Surfaces the patient's active subscriptions and routes them to Stripe's
// Customer Portal for cancel/payment-method/invoice actions. Same hook as
// the Billing page so behavior stays consistent.
function SubscriptionsCard({ patientId, hasStripe }: { patientId: string; hasStripe: boolean }) {
  const qc = useQueryClient();
  const treatmentsQ = useTreatments(patientId);
  const portal = useBillingPortal();
  // The cancel modal targets one treatment at a time.
  const [cancelTarget, setCancelTarget] = useState<{
    id: string; medication: string; nextChargeDate: string | null;
  } | null>(null);

  const activeSubs = (treatmentsQ.data ?? []).filter(
    (t) => t.status === "active" && t.stripe_subscription_id,
  );

  return (
    <Card title="Subscriptions">
      {portal.error && (
        <div className="mb-3">
          <Alert tone="error">
            {portal.error.message ||
              "Couldn't open billing portal. Please email care@fortehealth.co."}
          </Alert>
        </div>
      )}

      {!hasStripe ? (
        <p className="text-[14px] text-fh-gray m-0">
          You don't have any subscriptions on file yet. Once treatment begins, your active plans
          will appear here.
        </p>
      ) : (
        <>
          {activeSubs.length > 0 ? (
            <ul className="divide-y divide-fh-border mb-4 border border-fh-border rounded-[10px]">
              {activeSubs.map((t) => (
                <li key={t.id} className="flex justify-between items-center py-3 px-3.5 text-[13.5px] gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-fh-ink truncate">{t.medication}</div>
                    {t.current_period_end && (
                      <div className="text-[12.5px] text-fh-gray">
                        {t.cancel_at_period_end
                          ? `Cancels on ${new Date(t.current_period_end).toLocaleDateString()}`
                          : `Next charge ${new Date(t.current_period_end).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right font-semibold text-fh-ink">
                      {t.monthly_price !== null ? `$${Number(t.monthly_price).toFixed(2)}/mo` : "—"}
                    </div>
                    {!t.cancel_at_period_end && (
                      <button
                        type="button"
                        onClick={() => setCancelTarget({
                          id: t.id,
                          medication: t.medication,
                          nextChargeDate: t.current_period_end
                            ? new Date(t.current_period_end).toLocaleDateString()
                            : null,
                        })}
                        className="text-[12px] font-semibold text-fh-gray hover:text-red-700 underline"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-fh-gray mb-4">
              No active subscriptions right now. Past invoices and your card on file are still
              accessible via the secure billing portal.
            </p>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12.5px] text-fh-gray m-0 leading-relaxed">
              <strong className="text-fh-ink">Manage payment, view invoices, update card.</strong>{" "}
              The billing portal opens Stripe's secure page. Cancellations are handled in-app —
              use the Cancel link above. See our{" "}
              <a
                href="https://fortehealth.co/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fh-green hover:text-fh-green-dark underline font-semibold"
              >
                Refund Policy
              </a>
              .
            </p>
            <Button
              loading={portal.isPending}
              onClick={() => portal.mutate()}
              className="shrink-0"
            >
              Manage payment
            </Button>
          </div>
        </>
      )}

      <CancelSubscriptionModal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        treatmentId={cancelTarget?.id ?? ""}
        medicationLabel={cancelTarget?.medication ?? ""}
        nextChargeDate={cancelTarget?.nextChargeDate ?? null}
        onCancelled={() => {
          qc.invalidateQueries({ queryKey: ["treatments", patientId] });
        }}
      />
    </Card>
  );
}

function EmailCard({ currentEmail }: { currentEmail: string }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: currentEmail },
  });

  useEffect(() => { reset({ email: currentEmail }); }, [currentEmail, reset]);

  async function onSubmit(values: EmailForm) {
    setServerError(null);
    setSaved(false);
    if (values.email.toLowerCase() === currentEmail.toLowerCase()) {
      setEditing(false);
      return;
    }
    const { error } = await supabase.auth.updateUser({ email: values.email });
    if (error) { setServerError(error.message); return; }
    setSaved(true);
    setEditing(false);
  }

  return (
    <Card
      title="Sign-in email"
      action={!editing ? (
        <button
          type="button"
          onClick={() => { setEditing(true); setSaved(false); setServerError(null); }}
          className="text-[12px] font-semibold text-fh-green hover:text-fh-green-dark"
        >
          Change
        </button>
      ) : undefined}
    >
      {!editing ? (
        <>
          <p className="text-[14px] text-fh-ink">{currentEmail || "—"}</p>
          <p className="text-[12px] text-fh-gray mt-2">
            This is the email you use to sign in. Keep it current so you can reset your password.
          </p>
          {saved && <div className="mt-3"><Alert tone="success">Email updated.</Alert></div>}
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
          {serverError && <Alert tone="error">{serverError}</Alert>}
          <FormField
            label="New email"
            type="email"
            autoComplete="email"
            autoFocus
            error={errors.email?.message}
            {...register("email")}
          />
          <p className="text-[12px] text-fh-gray">
            You'll use this address to sign in the next time. Make sure it's one you actively check.
          </p>
          <div className="flex items-center gap-3 mt-1">
            <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
              Save email
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { reset(); setEditing(false); setServerError(null); }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function ProfileCard({ patient }: { patient: NonNullable<ReturnType<typeof usePatient>["data"]> }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: patient.first_name,
      last_name:  patient.last_name,
      phone:      patient.phone ?? "",
    },
  });

  async function onSubmit(values: ProfileForm) {
    setServerError(null);
    setSaved(false);
    const { error } = await supabase
      .from("patients")
      .update({
        first_name: values.first_name,
        last_name:  values.last_name,
        phone:      values.phone ?? null,
      })
      .eq("id", patient.id);
    if (error) { setServerError(error.message); return; }
    setSaved(true);
    setEditing(false);
    await qc.invalidateQueries({ queryKey: ["patient"] });
  }

  return (
    <Card
      title="Profile"
      action={!editing ? (
        <button
          type="button"
          onClick={() => { setEditing(true); setSaved(false); setServerError(null); }}
          className="text-[12px] font-semibold text-fh-green hover:text-fh-green-dark"
        >
          Edit
        </button>
      ) : undefined}
    >
      {!editing ? (
        <dl className="divide-y divide-fh-border">
          <Row label="Name" value={`${patient.first_name} ${patient.last_name}`.trim()} />
          <Row label="Phone" value={patient.phone ?? "—"} />
          <Row label="State" value={patient.state ?? "—"} />
          <Row label="Member since" value={new Date(patient.created_at).toLocaleDateString()} last />
          {saved && <div className="pt-3"><Alert tone="success">Profile updated.</Alert></div>}
        </dl>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
          {serverError && <Alert tone="error">{serverError}</Alert>}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name" {...register("first_name")} error={errors.first_name?.message} />
            <FormField label="Last name"  {...register("last_name")}  error={errors.last_name?.message} />
          </div>
          <FormField
            label="Phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="555-555-5555"
            {...register("phone")}
            error={errors.phone?.message}
          />
          <div className="flex items-center gap-3 mt-1">
            <Button type="submit" loading={isSubmitting} disabled={!isDirty}>Save</Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { reset(); setEditing(false); setServerError(null); }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={`flex justify-between py-[9px] text-[13.5px] ${last ? "" : ""}`}>
      <span className="text-fh-gray">{label}</span>
      <span className="font-semibold text-fh-ink text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}
